import { Component, OnInit, ViewEncapsulation, NgZone, ViewChild, ElementRef, Input, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MapsAPILoader } from 'src/app/shared/services/maps-api-loader.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { MediaObserver, MediaChange } from '@ngbracket/ngx-layout';
import { filter, map } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
declare var google: any;

@Component({
    selector: 'app-address-map',
    templateUrl: './address-map.component.html',
    styleUrls: ['./address-map.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class AddressMapComponent implements OnInit {

  longitude: any;
  latitude: any;
  entityObj: any;
  lat = 40.416394;
  lng = -3.703930;
  zoom = 10;
  markerLat = null;
  markerLng = null;
  mapsApiLoaded = false;
  private geoCoder: any;
  address: any;
  compAddress: any;
  coordinates = [] as any;
  mapHeight: any;
  mqAlias: string;
  loadingResults = false;

  @ViewChild('search', { static: true })
  public searchElementRef: ElementRef;
  private _unsubscribeAll: Subject<any>;

  constructor(
    public matDialogRef: MatDialogRef<AddressMapComponent>,
    private mapsAPILoader: MapsAPILoader,
    private mediaObserver: MediaObserver,
    private ngZone: NgZone,
    private http: HttpClient,
    @Inject(MAT_DIALOG_DATA) public _data: any
  ) {
    this._unsubscribeAll = new Subject();
  }

  ngOnInit(): void {
    // this.entityObj = ''
    // if (this.entityObj && this.entityObj.lat && this.entityObj.lng) {
    //   this.lat = parseFloat(this.entityObj.lat);
    //   this.lng = parseFloat(this.entityObj.lng);
    //   this.zoom = this.entityObj.zoom;
    // }
    this.mediaObserver.asObservable()
      .pipe(
        filter((changes: MediaChange[]) => changes.length > 0),
        map((changes: MediaChange[]) => changes[0])
      )
      .subscribe((change: MediaChange) => {
        this.mqAlias = change.mqAlias;
        let adjustScreen = 265;
        if (this.mqAlias === 'xl' || this.mqAlias === 'gt-lg') {
          adjustScreen = 500;
        }
        this.mapHeight = (window.innerHeight - adjustScreen) + 'px';
      });

    this.mapsAPILoader.load().then(() => {
      this.mapsApiLoaded = true;
      this.geoCoder = new google.maps.Geocoder();
      const addr = this._data.address;
      const hasAddress = !!addr && addr !== 'undefined' && addr !== 'null' && String(addr).trim() !== '';
      if (hasAddress) {
        this.loadingResults = true;
        this.loadAddress(addr);
      } else if (this._data.longitude?.value && this._data.latitude?.value) {
        this.markerLat = this._data.latitude.value;
        this.markerLng = this._data.longitude.value;
        this.getAddress(this.markerLat, this.markerLng);
      } else {
        // No address or coordinates yet — center on the visitor's location
        // instead of the hardcoded Spain default (lat/lng above). Applies to
        // every appAddressMap usage (contacts, leads, organization, quotes...).
        this.centerOnDefaultLocation();
      }
    });
  }

  mapClicked($event: any): void {
    const latLng = $event?.latLng;
    this.markerLat = typeof latLng?.lat === 'function' ? latLng.lat() : $event?.coords?.lat;
    this.markerLng = typeof latLng?.lng === 'function' ? latLng.lng() : $event?.coords?.lng;
    this.getAddress(this.markerLat, this.markerLng);
  }

  getAddress(latitude: any, longitude: any): void {
    this.geoCoder.geocode({ location: { lat: latitude, lng: longitude } }, (results: any, status: any) => {
      if (status === 'OK') {
        if (results[0]) {
          this.address = results[0].address_components;
          this.compAddress = results[0].formatted_address;
        } else {
          return;
        }
      } else {
        return;
      }
      this.coordinates['lat'] = latitude;
      this.coordinates['lng'] = longitude;
    });
  }

  loadAddress(address: string): void {
    this.geoCoder.geocode({ address: address }, (results: any, status: any) => {
      if (status === 'OK') {
        this.zoom = 13;
        this.address = results[0].address_components;
        this.compAddress = results[0].formatted_address;
        this.coordinates['lat'] = results[0].geometry.location.lat();
        this.coordinates['lng'] = results[0].geometry.location.lng();
        this.lat = results[0].geometry.location.lat();
        this.lng = results[0].geometry.location.lng();
        this.markerLat = results[0].geometry.location.lat();
        this.markerLng = results[0].geometry.location.lng();
        this.loadingResults = false;
      } else {
        this.loadingResults = false;
        return;
      }
    });
  }

  // Default map view when no address is selected. Priority:
  //  1) an explicit country passed by the caller (e.g. business profile's
  //     merchant country) → geocode it;
  //  2) otherwise the visitor's location via IP lookup (ipdata returns lat/lng
  //     directly, so no geocoding is needed).
  // Falls back to the hardcoded center only if neither resolves.
  private centerOnDefaultLocation(): void {
    const explicit = this._data?.defaultCountry;
    if (explicit) {
      this.loadCountryCenter(explicit);
      return;
    }
    this.http.get(`${environment.USER_MANAGEMENT_URL}/ipdata/lookup`).subscribe(
      (res: any) => {
        const lat = parseFloat(res?.latitude);
        const lng = parseFloat(res?.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          this.lat = lat;
          this.lng = lng;
          this.zoom = 5;
          // Drop a (draggable) marker at the detected location so the red pin
          // shows, and reverse-geocode it so "Pick Address" works right away.
          this.markerLat = lat;
          this.markerLng = lng;
          this.getAddress(lat, lng);
        } else if (res?.country_name || res?.country_code) {
          this.loadCountryCenter(res.country_name || res.country_code);
        }
      },
      () => { /* keep the default center on error */ },
    );
  }

  // Center the map on a country (no marker) — the default view when no address
  // is selected, so it reflects the merchant's country rather than Spain.
  loadCountryCenter(country: string): void {
    this.geoCoder.geocode({ address: country }, (results: any, status: any) => {
      if (status === 'OK' && results && results[0]) {
        this.lat = results[0].geometry.location.lat();
        this.lng = results[0].geometry.location.lng();
        this.zoom = 5; // country-level view
      }
    });
  }

  closeDialog(){
    this.matDialogRef.close()
  }

}

